import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import {
  Lightbulb,
  Thermometer,
  Wind,
  Tv,
  Lock,
  Bell,
  Sun,
  Moon,
  Power,
  Loader2,
  Home,
  ChevronRight,
  Minus,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const ROOM_SCENES = [
  { id: 'morning', name: 'Morning', icon: Sun, description: 'Bright lights, cool temp' },
  { id: 'work', name: 'Work', icon: Lightbulb, description: 'Focus lighting, comfortable' },
  { id: 'relax', name: 'Relax', icon: Tv, description: 'Dim lights, warm ambiance' },
  { id: 'sleep', name: 'Sleep', icon: Moon, description: 'Lights off, do not disturb' },
];

export default function GuestSmartRoomPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [activeReservation, setActiveReservation] = useState(null);
  const [roomDevices, setRoomDevices] = useState([]);
  const [activeScene, setActiveScene] = useState(null);
  const [deviceStates, setDeviceStates] = useState({
    lights: true,
    lightLevel: 70,
    ac: true,
    temperature: 22,
    tv: false,
    curtains: 'open',
    dnd: false,
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    loadRoomData();
  }, [authLoading, isAuthenticated, navigate, token]);

  const loadRoomData = async () => {
    try {
      setLoading(true);
      // Get active reservation
      const reservations = await guestPortalApi.listReservations(token);
      const active = reservations.data?.find(r => r.status === 'CHECKED_IN');
      
      if (active) {
        setActiveReservation(active);
        // In real implementation, would fetch actual device states from IoT backend
      }
    } catch (error) {
      console.error('Failed to load room data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceToggle = (device) => {
    setDeviceStates(prev => ({
      ...prev,
      [device]: !prev[device]
    }));
    toast.success(`${device.charAt(0).toUpperCase() + device.slice(1)} ${!deviceStates[device] ? 'turned on' : 'turned off'}`);
  };

  const handleValueChange = (device, value) => {
    setDeviceStates(prev => ({
      ...prev,
      [device]: value
    }));
  };

  const handleSceneSelect = (scene) => {
    setActiveScene(scene.id);
    
    // Apply scene presets
    switch (scene.id) {
      case 'morning':
        setDeviceStates({ lights: true, lightLevel: 100, ac: true, temperature: 21, tv: false, curtains: 'open', dnd: false });
        break;
      case 'work':
        setDeviceStates({ lights: true, lightLevel: 80, ac: true, temperature: 22, tv: false, curtains: 'open', dnd: false });
        break;
      case 'relax':
        setDeviceStates({ lights: true, lightLevel: 40, ac: true, temperature: 23, tv: true, curtains: 'closed', dnd: false });
        break;
      case 'sleep':
        setDeviceStates({ lights: false, lightLevel: 0, ac: true, temperature: 20, tv: false, curtains: 'closed', dnd: true });
        break;
    }
    
    toast.success(`Scene '${scene.name}' activated`);
  };

  const adjustTemperature = (delta) => {
    const newTemp = Math.min(28, Math.max(16, deviceStates.temperature + delta));
    setDeviceStates(prev => ({ ...prev, temperature: newTemp }));
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Skeleton className="h-10 w-48 mb-8" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  if (!activeReservation) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl text-center" data-testid="no-active-stay">
        <Home className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
        <h1 className="text-2xl font-bold mb-4">No Active Stay</h1>
        <p className="text-muted-foreground mb-6">
          Smart room controls are only available during your stay. 
          Once you check in, you'll be able to control room settings from here.
        </p>
        <Button onClick={() => navigate('/portal/dashboard')}>
          Go to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="guest-smart-room-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Home className="h-4 w-4" />
          <span>Room {activeReservation?.room_number || activeReservation?.room_type}</span>
        </div>
        <h1 className="text-3xl font-bold mb-2">Smart Room Controls</h1>
        <p className="text-muted-foreground">Control your room environment with ease</p>
      </div>

      {/* Quick Scenes */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Quick Scenes</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {ROOM_SCENES.map(scene => (
            <Card 
              key={scene.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                activeScene === scene.id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => handleSceneSelect(scene)}
              data-testid={`scene-${scene.id}`}
            >
              <CardContent className="pt-6 text-center">
                <scene.icon className={`h-8 w-8 mx-auto mb-2 ${
                  activeScene === scene.id ? 'text-primary' : 'text-muted-foreground'
                }`} />
                <p className="font-medium">{scene.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{scene.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Tabs defaultValue="climate" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="climate">Climate</TabsTrigger>
          <TabsTrigger value="lighting">Lighting</TabsTrigger>
          <TabsTrigger value="other">Other</TabsTrigger>
        </TabsList>

        {/* Climate Controls */}
        <TabsContent value="climate" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Thermometer className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Air Conditioning</CardTitle>
                    <CardDescription>Set your preferred temperature</CardDescription>
                  </div>
                </div>
                <Switch 
                  checked={deviceStates.ac}
                  onCheckedChange={() => handleDeviceToggle('ac')}
                  data-testid="ac-toggle"
                />
              </div>
            </CardHeader>
            {deviceStates.ac && (
              <CardContent>
                <div className="flex items-center justify-center gap-6">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => adjustTemperature(-1)}
                    disabled={deviceStates.temperature <= 16}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <div className="text-center">
                    <span className="text-5xl font-bold">{deviceStates.temperature}</span>
                    <span className="text-2xl">°C</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => adjustTemperature(1)}
                    disabled={deviceStates.temperature >= 28}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-4 flex justify-center gap-2">
                  {[18, 20, 22, 24, 26].map(temp => (
                    <Button
                      key={temp}
                      variant={deviceStates.temperature === temp ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleValueChange('temperature', temp)}
                    >
                      {temp}°
                    </Button>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900/30">
                    <Wind className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Ventilation</CardTitle>
                    <CardDescription>Fresh air circulation</CardDescription>
                  </div>
                </div>
                <Badge variant="outline">Auto</Badge>
              </div>
            </CardHeader>
          </Card>
        </TabsContent>

        {/* Lighting Controls */}
        <TabsContent value="lighting" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                    <Lightbulb className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Room Lights</CardTitle>
                    <CardDescription>Main room lighting</CardDescription>
                  </div>
                </div>
                <Switch 
                  checked={deviceStates.lights}
                  onCheckedChange={() => handleDeviceToggle('lights')}
                  data-testid="lights-toggle"
                />
              </div>
            </CardHeader>
            {deviceStates.lights && (
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span>Brightness</span>
                    <span className="font-medium">{deviceStates.lightLevel}%</span>
                  </div>
                  <Slider
                    value={[deviceStates.lightLevel]}
                    onValueChange={([value]) => handleValueChange('lightLevel', value)}
                    max={100}
                    step={10}
                    className="w-full"
                    data-testid="brightness-slider"
                  />
                </div>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                    <Sun className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Curtains</CardTitle>
                    <CardDescription>Control window blinds</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button 
                  variant={deviceStates.curtains === 'open' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => handleValueChange('curtains', 'open')}
                >
                  Open
                </Button>
                <Button 
                  variant={deviceStates.curtains === 'half' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => handleValueChange('curtains', 'half')}
                >
                  Half
                </Button>
                <Button 
                  variant={deviceStates.curtains === 'closed' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => handleValueChange('curtains', 'closed')}
                >
                  Closed
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Other Controls */}
        <TabsContent value="other" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Tv className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Television</CardTitle>
                    <CardDescription>Smart TV controls</CardDescription>
                  </div>
                </div>
                <Switch 
                  checked={deviceStates.tv}
                  onCheckedChange={() => handleDeviceToggle('tv')}
                  data-testid="tv-toggle"
                />
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                    <Bell className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Do Not Disturb</CardTitle>
                    <CardDescription>Block housekeeping and calls</CardDescription>
                  </div>
                </div>
                <Switch 
                  checked={deviceStates.dnd}
                  onCheckedChange={() => {
                    handleDeviceToggle('dnd');
                    toast.info(deviceStates.dnd ? 'Do Not Disturb disabled' : 'Do Not Disturb enabled - You won\'t be disturbed');
                  }}
                  data-testid="dnd-toggle"
                />
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                    <Lock className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Door Lock</CardTitle>
                    <CardDescription>Room access</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="bg-green-50 text-green-700">Locked</Badge>
              </div>
            </CardHeader>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
